<script setup>
import { getCurrentInstance, ref } from 'vue';
import KanbanNotesAPI from 'dashboard/api/kanbanNotes';

const props = defineProps({
  cardId: { type: Number, required: true },
  modelValue: { type: String, default: '' },
});

const emit = defineEmits(['update:modelValue']);

const instance = getCurrentInstance();
const inputId = `file-field-${instance.uid}`;

const uploading = ref(false);
const error = ref('');

const MAX_SIZE = 4_194_304; // 4MB

function fileNameFromUrl(url) {
  if (!url) return '';
  return decodeURIComponent(url.split('/').pop().split('?')[0]);
}

async function handleFile(file) {
  error.value = '';
  if (file.size > MAX_SIZE) {
    error.value = 'Arquivo muito grande (máx. 4 MB)';
    return;
  }
  uploading.value = true;
  try {
    const formData = new FormData();
    formData.append('content', '.');
    formData.append('files[]', file);
    const { data } = await KanbanNotesAPI.create(props.cardId, formData);
    const url = data?.files?.[0]?.url ?? '';
    if (!url) throw new Error('URL não retornada');
    emit('update:modelValue', url);
  } catch {
    error.value = 'Erro ao enviar arquivo. Tente novamente.';
  } finally {
    uploading.value = false;
  }
}

function onDrop(e) {
  const file = e.dataTransfer?.files?.[0];
  if (file) handleFile(file);
}

function onInputChange(e) {
  const file = e.target.files?.[0];
  if (file) handleFile(file);
}

function clear() {
  emit('update:modelValue', null);
}

const dropLabel = 'Clique ou arraste um arquivo';
</script>

<template>
  <div>
    <!-- Estado preenchido -->
    <div
      v-if="modelValue"
      class="flex items-center gap-1.5 text-sm text-n-brand"
    >
      <span class="i-lucide-paperclip size-3 flex-shrink-0" />
      <a
        :href="modelValue"
        target="_blank"
        rel="noopener noreferrer"
        class="truncate hover:underline"
      >
        {{ fileNameFromUrl(modelValue) }}
      </a>
      <button
        type="button"
        class="ml-auto text-n-slate-9 hover:text-n-ruby-9 transition-colors flex-shrink-0"
        @click="clear"
      >
        <span class="i-lucide-x size-3" />
      </button>
    </div>

    <!-- Estado de loading -->
    <div
      v-else-if="uploading"
      class="flex items-center justify-center h-16 border-2 border-dashed border-n-weak rounded-lg"
    >
      <span class="i-lucide-loader-circle size-5 text-n-slate-9 animate-spin" />
    </div>

    <!-- Estado vazio / área de drop -->
    <div v-else>
      <input
        :id="inputId"
        type="file"
        accept="image/*,audio/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
        class="sr-only"
        @change="onInputChange"
      />
      <label
        :for="inputId"
        class="flex flex-col items-center justify-center gap-1.5 h-16 border-2 border-dashed border-n-weak rounded-lg cursor-pointer hover:border-n-container transition-colors"
        @dragover.prevent
        @drop.prevent="onDrop"
        @click.stop
      >
        <span class="i-lucide-upload size-5 text-n-slate-9" />
        <span class="text-xs text-n-slate-10 text-center px-2">{{
          dropLabel
        }}</span>
      </label>
      <p v-if="error" class="text-xs text-n-ruby-9 mt-1">{{ error }}</p>
    </div>
  </div>
</template>
